import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Box,
  Tooltip,
  CircularProgress,
  Typography,
} from "@mui/material";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import moment from "moment";
import { generateFormData, getImageUrl } from "utils";
import { DocumentName } from "pages/UtilityConsumers/style";
import { UploadImage } from "./style";
import AddIcon from "@mui/icons-material/Add";
import { uniqueId } from "lodash";
import { toast } from "react-toastify";
import getFileExtension from "utils/getFileExtension";
import DateField from "../../../Components/Common/DatePicker";

import { usePostFireSafetyResolvedObservationPhotographsMutation } from "apis/FireSafety/FireSafetyObservation";
import { complianceStatusEnum } from "common/enums";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

const validFileFormats = ["jpg", "png", "jpeg"];

const ConsumerResolveDetails = ({
  t,
  isAdmin,
  handleClose,
  isResolved,
  previewButtonHandler,
  observationDetails,
}) => {
  const { id: observationId, disableResolveDetailField } = observationDetails;

  const [addResolvePhotographs, { isLoading: isResolvePhotographLoading }] =
    usePostFireSafetyResolvedObservationPhotographsMutation();

  const getComplienceStautsObj = complianceStatusEnum.find(
    (item) =>
      item.value ===
      observationDetails?.developerNoticeCompliance?.complianceStatus
  );

  const WORK_IN_PROGRESS = 2;

  const formik = useFormik({
    initialValues: {
      id: observationDetails?.developerNoticeCompliance?.id ?? null,
      complianceStatus: getComplienceStautsObj ?? null,
      targetCompletionDate: observationDetails?.developerNoticeCompliance
        ?.targetCompletionDate
        ? convertUTCDateToLocalDate(
            observationDetails?.developerNoticeCompliance?.targetCompletionDate
          )
        : null,
      correctiveAction:
        observationDetails?.developerNoticeCompliance?.correctiveAction ?? "",
      remark: observationDetails?.developerNoticeCompliance?.remark ?? "",
      resolvedPhotographs: observationDetails?.resolvedPhotographs ?? [],
    },

    validationSchema: Yup.object({
      correctiveAction: Yup.string().required("Corrective action is required"),

      complianceStatus: Yup.object()
        .nullable()
        .required("Compliance status is required"),

      targetCompletionDate: Yup.date()
        .nullable()
        .when("complianceStatus", {
          is: (val) => val?.value == WORK_IN_PROGRESS,
          then: (schema) =>
            schema.required("Target completion date is required"),
          otherwise: (schema) => schema.nullable(),
        }),

      resolvedPhotographs: Yup.array()
        .min(1, "At least one photograph is required")
        .required("Photographs are required"),
    }),

    onSubmit: async (values) => {
      const { complianceStatus, targetCompletionDate, resolvedPhotographs } =
        values;

      if (resolvedPhotographs.length <= 0) {
        return toast.error(
          "Please upload image and size should be less than 10 mb"
        );
      }

      const payload = {
        ...values,
        inspectionObservationId: observationId,
        complianceStatus: complianceStatus.value,
        targetCompletionDate:
          targetCompletionDate && targetCompletionDate.format("YYYY-MM-DD"),
        resolvedPhotographs: resolvedPhotographs.map(
          ({ id, file, photographUrl }) => ({
            id: file ? 0 : id,
            file: file ?? "",
            photographUrl: photographUrl ?? "",
          })
        ),
      };

      const formData = new FormData();
      generateFormData(formData, payload);

      await addResolvePhotographs(formData);

      handleClose?.();
    },
  });

  const { values, setFieldValue, handleSubmit } = formik;

  return (
    <>
      {!isAdmin && (
        <Grid container columnSpacing={2} rowSpacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={4}>
            <SelectField
              label={t("Compliance Status") + "*"}
              name="complianceStatus"
              disabled={disableResolveDetailField}
              formik={formik}
              value={values.complianceStatus || ""}
              options={complianceStatusEnum}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              onChange={(_, option) => {
                setFieldValue("complianceStatus", option ?? "");
                if (option?.value !== 2) {
                  setFieldValue("targetCompletionDate", null);
                }
              }}
            />
          </Grid>

          {values?.complianceStatus?.value === WORK_IN_PROGRESS && (
            <Grid item xs={12} sm={6} md={4}>
              <DateField
                label={t("Target Completion Date") + "*"}
                name="targetCompletionDate"
                disabled={disableResolveDetailField}
                minDate={moment()}
                maxDate={moment().add(120, "days")}
                disablePast
                formik={formik}
                format="DD-MM-YYYY"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <InputFieldText
              label={t("Corrective Action") + "*"}
              name="correctiveAction"
              disabled={disableResolveDetailField}
              formik={formik}
              placeholder="Enter corrective action"
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <InputFieldText
              label={t("Remark")}
              disabled={disableResolveDetailField}
              name="remark"
              formik={formik}
              placeholder="Enter remark"
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} className="mt-3">
        {(!isAdmin || (isAdmin && disableResolveDetailField)) && (
          <label className="fw-semibold h5">
            <p className="mb-1">{t("Resolve Photographs")}:</p>
          </label>
        )}

        <div className="d-flex gap-3 flex-wrap">
          {(!isAdmin || (isAdmin && disableResolveDetailField)) &&
            values.resolvedPhotographs.map(
              ({ photographUrl, name, id: currentId }, inx) => (
                <div className="image-container" key={inx}>
                  <a
                    onClick={() => previewButtonHandler(photographUrl)}
                    target="_blank"
                  >
                    <Box
                      component="img"
                      className="image d-block m-auto"
                      alt="image"
                      src={getImageUrl(photographUrl)}
                    />
                  </a>

                  {!isAdmin && !isResolved && !disableResolveDetailField && (
                    <i
                      className="ri-close-circle-line fs-3 close-btn"
                      onClick={() => {
                        setFieldValue(
                          "resolvedPhotographs",
                          values.resolvedPhotographs.filter(
                            ({ id }) => id !== currentId
                          )
                        );
                      }}
                    />
                  )}

                  <Tooltip title={name}>
                    <DocumentName fontSize={13} variant="body2">
                      {name}
                    </DocumentName>
                  </Tooltip>
                </div>
              )
            )}

          {!isAdmin && !isResolved && (
            <Box component="label" className="m-0 p-0 cursor-pointer">
              <UploadImage>
                <AddIcon sx={{ color: "black" }} />
              </UploadImage>

              <input
                type="file"
                accept="image pdf/*"
                multiple
                disabled={disableResolveDetailField}
                hidden
                onBlur={() =>
                  formik.setFieldTouched("resolvedPhotographs", true)
                }
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const isAllFileValid = files.every(
                    (file) =>
                      validFileFormats.includes(
                        getFileExtension(file.name.toLowerCase())
                      ) && file.size < 10485760
                  );

                  if (!isAllFileValid) {
                    toast.error(
                      "Please upload image and size should be less than 10 mb"
                    );
                  } else {
                    setFieldValue("resolvedPhotographs", [
                      ...values.resolvedPhotographs,
                      ...files.map((file) => ({
                        id: uniqueId("doc__"),
                        photographUrl: URL.createObjectURL(file),
                        name: file.name,
                        file: file,
                      })),
                    ]);
                  }
                  e.target.value = null;
                }}
              />
            </Box>
          )}
        </div>
        {formik.touched.resolvedPhotographs &&
          formik.errors.resolvedPhotographs && (
            <Typography
              variant="subtitle2"
              className="input-field-error-message"
              color="error"
            >
              {formik.errors.resolvedPhotographs}
            </Typography>
          )}
      </Grid>

      {!isAdmin && (
        <div className="d-flex justify-content-end mt-3 gap-2">
          <Button
            size="medium"
            variant="contained"
            type="submit"
            onClick={handleSubmit}
            disabled={isResolvePhotographLoading || disableResolveDetailField}
          >
            {t("Submit")}
            {isResolvePhotographLoading && <CircularProgress size={20} />}
          </Button>
        </div>
      )}
    </>
  );
};

export default ConsumerResolveDetails;
